import Medusa from "@medusajs/js-sdk";

type StringOrNone = null | undefined | string;

export const baseUrl = import.meta.env.VITE_MEDUSA_BACKEND_URL;

export const backendUrl = __BACKEND_URL__ ?? "/";

const decodeJwt = (token: string) => {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload));

    return decoded;
  } catch {
    return null;
  }
};

export const isTokenExpired = (token: string | null) => {
  if (!token) {
    return true;
  }

  const payload = decodeJwt(token);

  if (!payload?.exp) {
    return true;
  }

  return payload.exp * 1000 < Date.now();
};

export const getAuthToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem("medusa_auth_token");
};

export const sdk = new Medusa({
  baseUrl: backendUrl,
  auth: {
    type: "jwt",
  },
});

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== "undefined") {
  (window as any).__sdk = sdk;
}

export const mercurQuery = async (
  url: string,
  {
    method,
    body,
    query,
    headers,
    config,
  }: {
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    body?: BodyInit | object | string | null;
    query?: Record<string, string | number>;
    headers?: { [key: string]: string };
    config?: { [key: string]: any };
  },
) => {
  const headersAuthorization = headers?.authorization as StringOrNone;
  const configHeadersAuthorization = config?.headers
    ?.authorization as StringOrNone;
  const configCredentials = config?.credentials as
    | RequestCredentials
    | null
    | undefined;
  const bearer = window.localStorage.getItem("medusa_auth_token") || "";
  const params = Object.entries(query || {}).reduce(
    (acc, [key, value], index) => {
      if (value && value !== undefined) {
        const queryLength = Object.values(query || {}).filter(
          (i) => i && i !== undefined,
        ).length;
        acc += `${key}=${value}${index + 1 <= queryLength ? "&" : ""}`;
      }
      return acc;
    },
    "",
  );
  const finalUrl = /^(https?:)?\/\//i.test(url)
    ? url
    : baseUrl
      ? `${baseUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`
      : url;
  const init: RequestInit & {
    method: string;
    body: BodyInit | object | string | null;
    headers: { [key: string]: string };
  } = {
    method: method,
    body: body ? JSON.stringify(body) : null,
    ...config,
    headers: {
      authorization: `Bearer ${bearer}`,
      'Content-Type': 'application/json',
      ...headers,
      ...(config?.headers ?? {}),
    },
  };

  if (!(bearer || headersAuthorization || configHeadersAuthorization)) {
    init.credentials = configCredentials ?? "include";
    delete init.headers.authorization;
  }

  const response = await fetch(`${finalUrl}${params && `?${params}`}`, init);

  if (!response.ok) {
    const errorData = await response.json();

    if (response.status === 401) {
      if (isTokenExpired(token)) {
        localStorage.removeItem('medusa_auth_token');
        window.location.href = '/login?reason=Unauthorized';
        return;
      }

      throw {
        type: 'NO_PERMISSION',
        message: errorData.message || 'Unauthorized'
      };
    }

    const error = new Error(errorData.message || 'Server error');
    (error as Error & { status: number }).status = response.status;
    throw error;
  }

  return response.json();
};
